import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Badge, Chip, TextField, IconButton, Paper, Divider,
  CircularProgress, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Button, Alert
} from '@mui/material';
import {
  WhatsApp, Language, Send, Close, PersonAdd, Refresh,
  Circle
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

function getEmpresaId() {
  try {
    const user = JSON.parse(atob(token().split('.')[1]));
    return user.empresaId || user.id;
  } catch { return null; }
}

export default function Bandeja() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [filtro, setFiltro] = useState('ABIERTA');
  const [canal, setCanal] = useState('');
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const socketRef = useRef(null);

  // Conectar Socket.io
  useEffect(() => {
    const eid = getEmpresaId();
    if (!eid) return;

    const base = API || window.location.origin;
    // Conectar al namespace /chat — polling primero (más confiable a través de nginx)
    const chatSocket = io(`${base}/chat`, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      upgrade: true,
    });

    chatSocket.on('connect', () => {
      setConnected(true);
      chatSocket.emit('join_empresa', { empresa_id: eid });
    });

    chatSocket.on('disconnect', () => setConnected(false));
    chatSocket.on('connect_error', () => setConnected(false));

    chatSocket.on('nuevo_mensaje', (msg) => {
      // Si es la conversación activa, agregar mensaje
      setSelected(prev => {
        if (prev && prev.id === msg.conversacion_id) {
          setMensajes(m => [...m, msg]);
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
        return prev;
      });
      // Actualizar última actividad en la lista
      setConvs(prev => prev.map(c =>
        c.id === msg.conversacion_id
          ? { ...c, ultimo_mensaje: msg.contenido, ultimo_mensaje_at: msg.created_at, mensajes_no_leidos: msg.remitente === 'CONTACTO' ? (c.mensajes_no_leidos || 0) + 1 : c.mensajes_no_leidos }
          : c
      ).sort((a, b) => new Date(b.ultimo_mensaje_at || b.created_at) - new Date(a.ultimo_mensaje_at || a.created_at)));
    });

    chatSocket.on('nueva_conversacion', (conv) => {
      setConvs(prev => [conv, ...prev]);
    });

    socketRef.current = chatSocket;
    return () => { chatSocket.disconnect(); };
  }, []);

  useEffect(() => { loadConvs(); }, [filtro, canal]);

  useEffect(() => {
    if (selected) loadMensajes(selected.id);
  }, [selected?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  async function loadConvs() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtro) params.set('estado', filtro);
      if (canal) params.set('canal', canal);
      const res = await axios.get(`${API}/api/crm/bandeja?${params}`, { headers: headers() });
      setConvs(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando conversaciones');
    } finally { setLoading(false); }
  }

  async function loadMensajes(convId) {
    try {
      setLoadingMsgs(true);
      const res = await axios.get(`${API}/api/crm/conversaciones/${convId}/mensajes`, { headers: headers() });
      setMensajes(res.data.data || []);
      // Limpiar no leídos
      setConvs(prev => prev.map(c => c.id === convId ? { ...c, mensajes_no_leidos: 0 } : c));
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando mensajes');
    } finally { setLoadingMsgs(false); }
  }

  async function handleSend() {
    if (!texto.trim() || !selected || sending) return;
    const contenido = texto.trim();
    setTexto('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/crm/conversaciones/${selected.id}/mensajes`, { contenido }, { headers: headers() });
      setMensajes(prev => [...prev, res.data.data]);
    } catch (e) {
      setError(e.response?.data?.error || 'Error enviando mensaje');
      setTexto(contenido);
    } finally { setSending(false); }
  }

  async function handleCerrar() {
    if (!selected) return;
    try {
      await axios.put(`${API}/api/crm/conversaciones/${selected.id}/cerrar`, { estado: 'CERRADA' }, { headers: headers() });
      setConvs(prev => prev.map(c => c.id === selected.id ? { ...c, estado: 'CERRADA' } : c));
      setSelected(prev => ({ ...prev, estado: 'CERRADA' }));
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  const totalNoLeidos = convs.reduce((s, c) => s + (c.mensajes_no_leidos || 0), 0);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
      {/* Panel izquierdo — lista de conversaciones */}
      <Box sx={{ width: 320, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Bandeja {totalNoLeidos > 0 && <Badge badgeContent={totalNoLeidos} color="error" sx={{ ml: 1 }} />}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Circle sx={{ fontSize: 10, color: connected ? '#4caf50' : '#f44336' }} />
              <Typography variant="caption" color={connected ? 'success.main' : 'error.main'}>
                {connected ? 'En línea' : 'Desconectado'}
              </Typography>
              <IconButton size="small" onClick={loadConvs}><Refresh fontSize="small" /></IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select value={filtro} onChange={e => setFiltro(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ABIERTA">Abiertos</MenuItem>
                <MenuItem value="PENDIENTE">Pendientes</MenuItem>
                <MenuItem value="CERRADA">Cerrados</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select value={canal} onChange={e => setCanal(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                <MenuItem value="WEB">Chat Web</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Lista */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={32} /></Box>
        ) : convs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Sin conversaciones</Typography>
          </Box>
        ) : (
          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {convs.map(conv => (
              <ListItem key={conv.id} button selected={selected?.id === conv.id}
                onClick={() => setSelected(conv)}
                sx={{ borderBottom: '1px solid #f0f0f0', '&.Mui-selected': { bgcolor: '#e3f2fd' } }}>
                <ListItemAvatar>
                  <Badge badgeContent={conv.mensajes_no_leidos || 0} color="error" overlap="circular">
                    <Avatar sx={{ bgcolor: conv.canal === 'WHATSAPP' ? '#25d366' : '#005eff', width: 40, height: 40 }}>
                      {conv.canal === 'WHATSAPP' ? <WhatsApp fontSize="small" /> : <Language fontSize="small" />}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={conv.mensajes_no_leidos ? 700 : 400} noWrap sx={{ maxWidth: 150 }}>
                        {conv.contacto_nombre || conv.contacto_telefono}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {conv.ultimo_mensaje_at ? formatDistanceToNow(new Date(conv.ultimo_mensaje_at), { locale: es, addSuffix: false }) : ''}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="span">
                      {conv.campana_nombre && (
                        <Box component="span" sx={{ display: 'inline-block', fontSize: 9, fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100', borderRadius: 0.5, px: 0.5, py: 0.25, mr: 0.5, mb: 0.25 }}>
                          📣 {conv.campana_nombre}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" noWrap component="span" sx={{ display: 'block' }}>
                        {conv.ultimo_mensaje || '...'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Panel derecho — chat */}
      {selected ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header conversación */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: selected.canal === 'WHATSAPP' ? '#25d366' : '#005eff' }}>
                {selected.canal === 'WHATSAPP' ? <WhatsApp /> : <Language />}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{selected.contacto_nombre}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {selected.contacto_telefono && <Typography variant="caption" color="text.secondary">{selected.contacto_telefono}</Typography>}
                  {selected.contacto_email && <Typography variant="caption" color="text.secondary">{selected.contacto_email}</Typography>}
                  <Chip label={selected.canal} size="small" sx={{ height: 16, fontSize: 10,
                    bgcolor: selected.canal === 'WHATSAPP' ? '#e8f5e9' : '#e3f2fd',
                    color: selected.canal === 'WHATSAPP' ? '#2e7d32' : '#1565c0' }} />
                  <Chip label={selected.estado} size="small" sx={{ height: 16, fontSize: 10 }}
                    color={selected.estado === 'ABIERTA' ? 'success' : selected.estado === 'PENDIENTE' ? 'warning' : 'default'} />
                  {selected.campana_nombre && (
                    <Chip label={`📣 ${selected.campana_nombre}`} size="small"
                      sx={{ height: 16, fontSize: 10, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selected.estado === 'ABIERTA' && (
                <Button size="small" variant="outlined" color="error" startIcon={<Close />} onClick={handleCerrar}>
                  Cerrar
                </Button>
              )}
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ m: 1 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Mensajes */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {loadingMsgs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={28} /></Box>
            ) : mensajes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>Sin mensajes aún</Typography>
            ) : (
              mensajes.map(msg => (
                <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.remitente === 'AGENTE' ? 'flex-end' : 'flex-start' }}>
                  <Paper sx={{
                    px: 1.5, py: 1, maxWidth: '70%', borderRadius: 2,
                    bgcolor: msg.remitente === 'AGENTE' ? '#005eff' : 'white',
                    color: msg.remitente === 'AGENTE' ? 'white' : 'inherit',
                    boxShadow: 1,
                  }}>
                    {msg.remitente === 'AGENTE' && msg.agente_nombre && (
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontWeight: 600 }}>{msg.agente_nombre}</Typography>
                    )}
                    <Typography variant="body2">{msg.contenido}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.3 }}>
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </Typography>
                  </Paper>
                </Box>
              ))
            )}
            <div ref={endRef} />
          </Box>

          {/* Input respuesta */}
          {selected.estado !== 'CERRADA' && (
            <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: 'white', display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" placeholder="Escribe un mensaje..." multiline maxRows={4}
                value={texto} onChange={e => setTexto(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
              <IconButton color="primary" onClick={handleSend} disabled={!texto.trim() || sending}
                sx={{ bgcolor: '#005eff', color: 'white', '&:hover': { bgcolor: '#0043ca' }, '&:disabled': { bgcolor: '#e0e0e0' } }}>
                {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Send />}
              </IconButton>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
          <Box sx={{ textAlign: 'center' }}>
            <WhatsApp sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Selecciona una conversación</Typography>
            <Typography variant="body2" color="text.disabled">Los mensajes de WhatsApp y Chat Web aparecen aquí</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
